import { createContext, useContext } from 'react'

export type OwnerRole = 'founder' | 'support' | 'finance' | 'readonly'

export const VALID_OWNER_ROLES: OwnerRole[] = ['founder', 'support', 'finance', 'readonly']

export type OwnerCapability =
  | 'home'
  | 'workspaces:read'
  | 'workspaces:mutate'
  | 'people:read'
  | 'people:mutate'
  | 'support:read'
  | 'support:reply'
  | 'broadcast:send'
  | 'feedback:author'
  | 'experience:tune'
  | 'featureFlags:read'
  | 'featureFlags:flip'
  | 'economy:defaults'
  | 'credits:grant'
  | 'credits:refund'
  | 'templates:manage'
  | 'moderation'
  | 'ops'
  | 'revenue:read'
  | 'breakglass'
  | 'activity'
  | 'actions:mutate'
  | 'roles:manage'
  | 'logs:read'
  | 'planLimits:edit'

export interface RolesContextValue {
  role: OwnerRole
  can: (capability: OwnerCapability) => boolean
}

export const ROLE_CAPABILITIES: Record<OwnerRole, Set<OwnerCapability>> = {
  founder: new Set<OwnerCapability>([
    'home',
    'workspaces:read',
    'workspaces:mutate',
    'people:read',
    'people:mutate',
    'support:read',
    'support:reply',
    'broadcast:send',
    'feedback:author',
    'experience:tune',
    'featureFlags:read',
    'featureFlags:flip',
    'economy:defaults',
    'credits:grant',
    'credits:refund',
    'templates:manage',
    'moderation',
    'ops',
    'revenue:read',
    'breakglass',
    'activity',
    'actions:mutate',
    'roles:manage',
    'logs:read',
    'planLimits:edit',
  ]),
  support: new Set<OwnerCapability>([
    'home',
    'workspaces:read',
    'workspaces:mutate',
    'people:read',
    'people:mutate',
    'support:read',
    'support:reply',
    'featureFlags:read',
    'credits:grant',
    'moderation',
    'ops',
    'actions:mutate',
  ]),
  finance: new Set<OwnerCapability>([
    'home',
    'workspaces:read',
    'workspaces:mutate',
    'people:read',
    'people:mutate',
    'featureFlags:read',
    'credits:grant',
    'credits:refund',
    'ops',
    'revenue:read',
    'actions:mutate',
  ]),
  readonly: new Set<OwnerCapability>([
    'home',
    'workspaces:read',
    'people:read',
    'support:read',
    'featureFlags:read',
    'templates:manage',
    'moderation',
    'ops',
    'revenue:read',
  ]),
}

export const RolesContext = createContext<RolesContextValue>({
  role: 'founder',
  can: () => true,
})

export function useRoles(): RolesContextValue {
  return useContext(RolesContext)
}
